import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
  FACTSET_ID_LOOKUP_ERROR_CODES,
  FACTSET_ID_LOOKUP_MAX_LIMIT,
  FACTSET_ID_LOOKUP_MIN_LIMIT,
  FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES,
} from "../shared/factset/factsetConstants";
import {
  executeFactSetIdLookup,
  IdLookupFailedError,
  IdLookupReauthRequiredError,
  IdLookupTimeoutError,
  type IdLookupFilterBlock,
  type IdLookupFilters,
  type IdLookupRequest,
  type IdLookupResponse,
} from "./services/factset/idLookupApi";
import {
  describeIdLookupUnavailable,
  resolveIdLookupAvailability,
} from "./idLookupMode";
import {
  ID_LOOKUP_MAX_NONBLANK_ROWS,
  ID_LOOKUP_WORKLOAD_WAVE_SIZE,
} from "./executorLimits";

export const ID_LOOKUP_TOOL_NAME = "id_lookup";
const ID_LOOKUP_UNAVAILABLE_ERROR_CODE = "id_lookup_unavailable";
/** Maximum worksheet rows processed by one registered lookup wave. */
export const ID_LOOKUP_MAX_CALLS_PER_WAVE = ID_LOOKUP_WORKLOAD_WAVE_SIZE;

export const ID_LOOKUP_EQUITY_TYPES = [
  "EQ_PRIMARY",
  "EQ_PREF",
  "EQ_DR_ADR",
  "EQ_DR_GDR",
  "EQ_DR_NVDR",
  "EQ_DR_ODR",
  "EQ_DEPOS",
  "EQ_WARR",
  "EQ_RIGHTS",
  "EQUITY",
  "EQUITY_DEFAULT",
] as const;

/**
 * Wire spelling per lowercase form, so a model that copies FactSet's own
 * `EQ_Primary` casing from the user guide is not rejected.
 */
const EQUITY_TYPES_BY_LOWERCASE = new Map<string, string>(
  ID_LOOKUP_EQUITY_TYPES.map((value) => [value.toLowerCase(), value]),
);

const FILTER_BLOCK_SCHEMA = {
  type: ["object", "null"],
  properties: {
    equityType: {
      type: ["array", "null"],
      description:
        "FactSet equity listing types. Leave null unless the user explicitly requests a non-default listing type.",
      items: {
        type: "string",
        enum: [...ID_LOOKUP_EQUITY_TYPES],
      },
    },
    region: {
      type: ["array", "null"],
      description:
        "ISO-2 listing-region codes. Leave null unless the user names a specific exchange or listing country.",
      items: {
        type: "string",
        pattern: "^[A-Za-z]{2}$",
      },
    },
  },
  required: ["equityType", "region"],
  additionalProperties: false,
} as const;

export const ID_LOOKUP_TOOL: ChartCapabilityToolDef = {
  name: ID_LOOKUP_TOOL_NAME,
  description:
    "Authoritative low-latency lookup for one company name, ticker, or identifier. " +
    "Returns FactSet-ranked public and private company candidates with tickers, " +
    "region-qualified IDs, entity IDs, ultimate-parent IDs/names, and each " +
    "candidate's domicile country and website. It does not " +
    "return listing or security IDs. Never silently choose an ambiguous candidate. " +
    "With filters=null, prefer the home-market primary common stock; only pass " +
    "filters when the user explicitly requests another listing type or region.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      search_text: {
        type: "string",
        minLength: 1,
        description: "One company name, ticker, ISIN, CUSIP, SEDOL, or FactSet identifier.",
      },
      filters: {
        type: ["object", "null"],
        description:
          "Optional listing filters. Pass null for the normal public-and-private lookup.",
        properties: {
          include: FILTER_BLOCK_SCHEMA,
          exclude: FILTER_BLOCK_SCHEMA,
        },
        required: ["include", "exclude"],
        additionalProperties: false,
      },
      limit: {
        type: "integer",
        minimum: FACTSET_ID_LOOKUP_MIN_LIMIT,
        maximum: FACTSET_ID_LOOKUP_MAX_LIMIT,
        description:
          `Candidates per dataset. Pass ${FACTSET_ID_LOOKUP_DEFAULT_LIMIT} unless the user explicitly asks for another number.`,
      },
    },
    required: ["search_text", "filters", "limit"],
    additionalProperties: false,
  },
};

const ID_LOOKUP_INPUT_ERROR_NAME = "IdLookupInputError";

class IdLookupInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = ID_LOOKUP_INPUT_ERROR_NAME;
    // Restore the prototype chain under ES5 target — `super(message)` would
    // otherwise leave `instanceof IdLookupInputError` returning false, so
    // argument mistakes would be classified as lookup failures and routed to
    // research instead of a corrected `id_lookup` retry.
    Object.setPrototypeOf(this, IdLookupInputError.prototype);
  }
}

function isIdLookupInputError(error: unknown): boolean {
  return (
    error instanceof IdLookupInputError ||
    (error instanceof Error && error.name === ID_LOOKUP_INPUT_ERROR_NAME)
  );
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function parseStringArray(
  rawValue: unknown,
  field: "equityType" | "region",
  blockName: "include" | "exclude",
): string[] | null {
  if (rawValue == null) return null;
  if (
    !Array.isArray(rawValue) ||
    rawValue.some((value) => typeof value !== "string" || !value.trim())
  ) {
    throw new IdLookupInputError(
      `filters.${blockName}.${field} must be an array of non-empty strings or null.`,
    );
  }
  const trimmed = rawValue.map((value) => (value as string).trim());
  if (field === "equityType") {
    const invalid = trimmed.find(
      (value) => !EQUITY_TYPES_BY_LOWERCASE.has(value.toLowerCase()),
    );
    if (invalid) {
      throw new IdLookupInputError(
        `filters.${blockName}.equityType contains unsupported value "${invalid}".`,
      );
    }
  } else {
    const invalid = trimmed.find((value) => !/^[A-Za-z]{2}$/.test(value));
    if (invalid) {
      throw new IdLookupInputError(
        `filters.${blockName}.region contains invalid region "${invalid}".`,
      );
    }
  }
  const values = Array.from(
    new Set(
      trimmed.map((value) =>
        field === "equityType"
          ? (EQUITY_TYPES_BY_LOWERCASE.get(value.toLowerCase()) as string)
          : value.toUpperCase(),
      ),
    ),
  );
  return values.length > 0 ? values : null;
}

function parseFilterBlock(
  rawValue: unknown,
  blockName: "include" | "exclude",
): IdLookupFilterBlock | null {
  if (rawValue == null) return null;
  if (typeof rawValue !== "object" || Array.isArray(rawValue)) {
    throw new IdLookupInputError(`filters.${blockName} must be an object or null.`);
  }
  const record = rawValue as Record<string, unknown>;
  const unknownKey = Object.keys(record).find(
    (key) => key !== "equityType" && key !== "region",
  );
  if (unknownKey) {
    throw new IdLookupInputError(
      `filters.${blockName}.${unknownKey} is not supported for company lookup.`,
    );
  }
  return {
    equityType: parseStringArray(record.equityType, "equityType", blockName),
    region: parseStringArray(record.region, "region", blockName),
  };
}

function hasValues(
  block: IdLookupFilterBlock | null,
  field: "equityType" | "region",
): boolean {
  return Array.isArray(block?.[field]) && block[field]!.length > 0;
}

function parseFilters(rawValue: unknown): IdLookupFilters | null {
  if (rawValue == null) return null;
  if (typeof rawValue !== "object" || Array.isArray(rawValue)) {
    throw new IdLookupInputError("filters must be an object or null.");
  }
  const record = rawValue as Record<string, unknown>;
  const unknownKey = Object.keys(record).find(
    (key) => key !== "include" && key !== "exclude",
  );
  if (unknownKey) {
    throw new IdLookupInputError(`filters.${unknownKey} is not supported.`);
  }
  const include = parseFilterBlock(record.include, "include");
  const exclude = parseFilterBlock(record.exclude, "exclude");
  for (const field of ["equityType", "region"] as const) {
    if (hasValues(include, field) && hasValues(exclude, field)) {
      throw new IdLookupInputError(
        `filters.${field} cannot be present in both include and exclude.`,
      );
    }
  }
  return include || exclude ? { include, exclude } : null;
}

function parseInput(rawInput: unknown): IdLookupRequest {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    throw new IdLookupInputError("id_lookup requires an object input.");
  }
  const record = rawInput as Record<string, unknown>;
  const unknownKey = Object.keys(record).find(
    (key) => key !== "search_text" && key !== "filters" && key !== "limit",
  );
  if (unknownKey) {
    throw new IdLookupInputError(`Unknown id_lookup field: ${unknownKey}.`);
  }
  const searchText =
    typeof record.search_text === "string" ? record.search_text.trim() : "";
  if (!searchText) {
    throw new IdLookupInputError("Missing required parameter: search_text");
  }
  if (utf8ByteLength(searchText) > FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES) {
    throw new IdLookupInputError(
      `search_text exceeds the ${FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES}-byte limit.`,
    );
  }
  if (
    !Number.isInteger(record.limit) ||
    (record.limit as number) < FACTSET_ID_LOOKUP_MIN_LIMIT ||
    (record.limit as number) > FACTSET_ID_LOOKUP_MAX_LIMIT
  ) {
    throw new IdLookupInputError(
      `limit must be an integer between ${FACTSET_ID_LOOKUP_MIN_LIMIT} and ${FACTSET_ID_LOOKUP_MAX_LIMIT}.`,
    );
  }
  if (!Object.prototype.hasOwnProperty.call(record, "filters")) {
    throw new IdLookupInputError(
      "Missing required parameter: filters (pass null for the default lookup).",
    );
  }
  return {
    search_text: searchText,
    filters: parseFilters(record.filters),
    limit: record.limit as number,
  };
}

function idLookupFailure(
  searchText: string,
  error: string,
  errorCode?: string,
  reauthorizationUrl?: string,
): IdLookupResponse & { reauthorization_url?: string } {
  return {
    success: false,
    status: "error",
    search_text: searchText,
    datasets: ["equities", "privateCompanies"],
    candidate_count: 0,
    candidates: [],
    parent_enrichment_status: "unavailable",
    fallback_recommended: true,
    fallback_reason: "id_lookup_failed",
    error_code: errorCode ?? null,
    error,
    ...(reauthorizationUrl ? { reauthorization_url: reauthorizationUrl } : {}),
  };
}

function idLookupValidationFailure(
  searchText: string,
  error: string,
): ReturnType<typeof idLookupFailure> {
  return {
    ...idLookupFailure(
      searchText,
      error,
      FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_REQUEST,
    ),
    fallback_recommended: false,
    fallback_reason: null,
  };
}

function buildObservationMetadata(result: IdLookupResponse): Record<string, unknown> {
  const publicCount = result.candidates.filter(
    (candidate) => candidate.company_type === "public",
  ).length;
  const privateCount = result.candidates.filter(
    (candidate) => candidate.company_type === "private",
  ).length;
  return {
    lookupPath: "direct_factset",
    candidateCount: result.candidate_count,
    publicCandidateCount: publicCount,
    privateCandidateCount: privateCount,
    parentEnrichmentStatus: result.parent_enrichment_status,
    fallbackRecommended: result.fallback_recommended,
    ...(result.fallback_reason ? { fallbackReason: result.fallback_reason } : {}),
    ...(result.meta?.lookup_request_id
      ? { lookupRequestId: result.meta.lookup_request_id }
      : {}),
    retryCount:
      Number(result.meta?.id_lookup_retry_count ?? 0) +
      Number(result.meta?.entity_reference_retry_count ?? 0),
    ...(typeof result.meta?.duration_ms === "number"
      ? { upstreamDurationMs: result.meta.duration_ms }
      : {}),
  };
}

export async function executeIdLookupToolDetailed(
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<{ output: string; observationMetadata?: Record<string, unknown> }> {
  let searchText = "";
  try {
    const request = parseInput(rawInput);
    searchText = request.search_text;
    // The live executor gates tool exposure; retain a local guard for any
    // direct dispatcher call made outside that path.
    const availability = await resolveIdLookupAvailability();
    if (!availability.available) {
      const failure = idLookupFailure(
        searchText,
        describeIdLookupUnavailable(availability),
        ID_LOOKUP_UNAVAILABLE_ERROR_CODE,
      );
      return {
        output: JSON.stringify(failure),
        observationMetadata: {
          lookupPath: "direct_factset",
          candidateCount: 0,
          parentEnrichmentStatus: "unavailable",
          fallbackRecommended: true,
          fallbackReason: ID_LOOKUP_UNAVAILABLE_ERROR_CODE,
          errorCode: ID_LOOKUP_UNAVAILABLE_ERROR_CODE,
        },
      };
    }
    const result = await executeFactSetIdLookup(request, { signal });
    const { meta: _meta, ...modelResult } = result;
    return {
      output: JSON.stringify(modelResult),
      observationMetadata: buildObservationMetadata(result),
    };
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    let failure: ReturnType<typeof idLookupFailure>;
    if (isIdLookupInputError(error)) {
      failure = idLookupValidationFailure(searchText, (error as Error).message);
    } else if (error instanceof IdLookupReauthRequiredError) {
      failure = idLookupFailure(
        searchText,
        error.message,
        error.serverErrorCode,
        error.reauthorizationUrl,
      );
    } else if (error instanceof IdLookupTimeoutError) {
      failure = idLookupFailure(
        searchText,
        error.message,
        FACTSET_ID_LOOKUP_ERROR_CODES.TIMEOUT,
      );
    } else if (error instanceof IdLookupFailedError) {
      failure = idLookupFailure(
        searchText,
        error.message,
        error.serverErrorCode,
      );
    } else {
      failure = idLookupFailure(
        searchText,
        error instanceof Error ? error.message : String(error),
      );
    }
    return {
      output: JSON.stringify(failure),
      observationMetadata: {
        lookupPath: "direct_factset",
        candidateCount: 0,
        parentEnrichmentStatus: "unavailable",
        fallbackRecommended: failure.fallback_recommended,
        ...(failure.fallback_reason
          ? { fallbackReason: failure.fallback_reason }
          : {}),
        ...(failure.error_code ? { errorCode: failure.error_code } : {}),
      },
    };
  }
}

export async function executeIdLookupTool(
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  return (await executeIdLookupToolDetailed(rawInput, signal)).output;
}

/**
 * Capability-gated executor guidance for direct and worksheet lookup paths.
 */
export function getIdLookupToolInstruction(
  options: {
    includeDirectLookup?: boolean;
    includeWorksheetWorkload?: boolean;
  } = {},
): string {
  const includeWorksheetWorkload =
    options.includeWorksheetWorkload === true;
  const includeDirectLookup =
    options.includeDirectLookup ?? !includeWorksheetWorkload;
  const directGuidance = includeDirectLookup
    ? `
## Company identity and ultimate-parent lookup (\`id_lookup\`)

- \`id_lookup\` is the authoritative first tool when the user asks for one company's ticker/stock symbol, FactSet entity ID (also called a company ID), ultimate-parent ID/name, or domicile country. The user does not need to mention FactSet or \`id_lookup\`.
- \`id_lookup\` does not return listing or security IDs. Leave those to research rather than reporting \`id\` in their place.
- Pass exactly one company name, ticker, or identifier per call. When a request names several companies, call \`id_lookup\` once for each of them, and never report an identifier for a company you did not look up.
- Normally pass \`filters: null\` and \`limit: ${FACTSET_ID_LOOKUP_DEFAULT_LIMIT}\`. Only filter when the user explicitly names a listing type or listing country.
- Report \`ticker\` as the user-facing stock symbol, and pass \`id\` back to other FactSet services as the authoritative identifier.
- Results remain in FactSet rank order. Select a candidate only when an exact identifier or the user's company/type/country evidence clearly supports it. Never silently choose rank 1 or combine fields from different candidates. If several candidates remain plausible, present their distinctions and ask the user instead of auto-selecting one.
- Do not call the branded research tool for the same identity intent when one candidate contains every specifically requested field. A coarse parent-enrichment fallback flag does not justify research for an already-returned ticker or entity ID.
- Use the branded research tool only when the runtime routing reminder confirms that a specifically requested field is still missing after lookup. Ask only for that missing identity fact.
- Examples: "What is Apple's ticker?", "Find Apple's entity ID", and "Who is Apple's ultimate parent company?" all require \`id_lookup\` first.`
    : "";
  const worksheetGuidance = includeWorksheetWorkload
    ? `

## Worksheet company identity lookup

- Call \`start_id_lookup_workload\` once with the complete one-column source range, aligned target range, and exactly the requested output fields. A domicile-country column is one of those fields: resolve it here rather than searching documents for it.
- Process at most ${ID_LOOKUP_MAX_CALLS_PER_WAVE} rows per wave by alternating \`lookup_next_id_lookup_wave\` and \`commit_id_lookup_wave\` until \`complete=true\`. The workload accepts at most ${ID_LOOKUP_MAX_NONBLANK_ROWS} nonblank rows.
- Commit one flat resolution per returned row: choose a zero-based \`candidateIndex\` and set \`unresolvedReason=null\`, or set \`candidateIndex=null\` with one closed unresolved reason. A lone candidate may still be \`ambiguous\` when the user's evidence does not support selecting it.
- Never author output values, combine fields across candidates, silently choose rank 1, or choose a candidate missing a requested field.
- Every duplicate worksheet row remains part of the commit and read-back even when its normalized lookup result is reused. Do not finish or mark the step terminal while rows remain pending, uncommitted, or unverified.
- When the user asks whether a worksheet column already states the right company (for example "check whether column B is the ultimate parent" or "flag the rows that disagree"), resolve the companies first, then call \`compare_company_name_columns\` once per row block to write the verdict and reason. Never judge those pairs in your own reasoning: worksheet reads are summarized out of the transcript, so hand-authored verdicts drift from the cells, and accents, legal forms, annotation suffixes, acronyms, and renamed companies are decided there deterministically.`
    : "";
  return `${directGuidance}${worksheetGuidance}`;
}

export const _test = {
  buildObservationMetadata,
  parseFilters,
  parseInput,
};
